// EPUB 3 (`EpubWriter`, SuiteCompile/EpubWriter.swift and `samplePage` from Publish.swift): a file per part, chapter
// and book-matter page, navigation and accessibility metadata, the reader's mark on a beta reader's copy.
import { BookMatter, ISBN, isbnOf, trimCFWhitespace, verseLines, type Block, type Inline } from '@writing-suite/core';
import { authorNames, BOOK_PAGE_KINDS, bylineName, heading, isListedInContents, numberWords, titlePageContent } from './manuscript.ts';
import { listTags, noteID, swiftNumber } from './html.ts';
import type { CompiledManuscript, RenderSettings, SectionRole } from './types.ts';
import { xmlEscape as esc } from './xml.ts';
import { ZipWriter } from './zip.ts';

interface Chapter {
  file: string;
  title: string;
  role: SectionRole;
  html: string;
  matter: BookMatter | null;
  /** The documents whose text is in this file, so a check on the file can name its source. */
  fragmentIDs: string[];
}

/** `EpubWriter.files`: each content file with its title and the documents in it. */
export function epubFiles(m: CompiledManuscript): { file: string; title: string; fragmentIDs: string[] }[] {
  const files = chapters(m).map((c) => ({ file: c.file, title: c.title, fragmentIDs: c.fragmentIDs }));
  if (titlePageContent(m)) files.unshift({ file: 'title.xhtml', title: m.title, fragmentIDs: [] });
  return files;
}

/** EPUB structural semantics for book matter (EPUB 3 SSV). */
function epubType(matter: BookMatter | null, role: SectionRole): string {
  switch (matter) {
    case 'half-title': return 'halftitlepage';
    case 'title-page': return 'titlepage';
    case 'copyright': return 'copyright-page';
    case 'dedication': return 'dedication';
    case 'epigraph': return 'epigraph';
    case 'contents': return 'toc';
    case 'acknowledgements': return 'acknowledgments';
    default: return role === 'part' ? 'part' : role === 'front-matter' ? 'frontmatter' : role === 'back-matter' ? 'backmatter' : 'chapter';
  }
}

const fileName = (n: number) => `section-${String(n).padStart(3, '0')}.xhtml`;

/** The book split into files, headings and text in place (the Contents list and reader mark are added by `write`). */
function chapters(m: CompiledManuscript): Chapter[] {
  const r = m.preset.render;
  const out: Chapter[] = [];
  let current: Chapter | null = null;

  const startChapter = (role: SectionRole, headingLines: string[], number = 0, title = '') => {
    if (current) out.push(current);
    // An empty heading or Contents link fails epubcheck: it's the title, else numbered.
    let lines = headingLines.filter((l) => trimCFWhitespace(l) !== '');
    if (!lines.length) {
      lines = [trimCFWhitespace(title) !== '' ? trimCFWhitespace(title) : role === 'part' || role === 'chapter'
        ? `${role === 'part' ? 'Part' : 'Chapter'} ${numberWords(Math.max(1, number))}`
        : m.title === '' ? 'Untitled' : m.title];
    }
    const tag = role === 'part' ? 'h1' : 'h2';
    const headingHTML = lines.map((line, i) => (i === 0 ? `<${tag}>${esc(line)}</${tag}>` : `<p class="subtitle">${esc(line)}</p>`)).join('\n');
    current = {
      file: fileName(out.length + 1), title: lines.join(': '), role, matter: null, fragmentIDs: [],
      html: `<section epub:type="${role === 'part' ? 'part' : 'chapter'}" role="doc-${role === 'part' ? 'part' : 'chapter'}">\n${headingHTML}\n`,
    };
  };

  m.sections.forEach((section, index) => {
    const scope = String(index + 1);
    if (section.matter !== null && BOOK_PAGE_KINDS.has(section.matter)) {
      if (current) out.push(current);
      current = null;
      const file = fileName(out.length + 1);
      const type = epubType(section.matter, section.role);
      let body = blocksHTML(section.blocks.filter((b) => b.kind.type !== 'sceneBreak'), true, scope);
      const title = trimCFWhitespace(section.title) === '' ? BookMatter.title(section.matter) : section.title;
      if (section.matter === 'contents') body = `<h1>${esc(title)}</h1>\n<!--contents-->`;
      out.push({ file, title, role: section.role, html: `<section epub:type="${type}" class="${section.matter}">\n${body}\n`,
        matter: section.matter, fragmentIDs: section.fragmentID !== null ? [section.fragmentID] : [] });
      return;
    }
    switch (section.role) {
      case 'part':
        startChapter('part', heading(r.partHeading, section), section.number, section.title);
        break;
      case 'chapter': case 'front-matter': case 'back-matter': {
        const lines = section.role === 'chapter' ? heading(r.chapterHeading, section) : [section.title];
        startChapter(section.role, lines, section.number, section.title);
        if (section.role !== 'chapter') {
          current!.matter = section.matter;
          current!.html = current!.html.replaceAll('epub:type="chapter" role="doc-chapter"', `epub:type="${epubType(section.matter, section.role)}"`);
        }
        break;
      }
      default:
        if (!current) startChapter('chapter', [m.title]);
        if (section.followsScene) current!.html += '<hr class="scene-break"/>\n';
    }
    const c = current!;
    for (const id of [...(section.fragmentID !== null ? [section.fragmentID] : []), ...(section.joined ?? [])]) if (!c.fragmentIDs.includes(id)) c.fragmentIDs.push(id);
    if (section.blocks.length) c.html += blocksHTML(section.blocks, false, scope) + '\n';
  });
  if (current) out.push(current);
  return resolvingDocumentLinks(out);
}

/** A link to a document as `inlines` writes it: group 1 the id (escaped), group 2 its words. */
const DOCUMENT_LINK = /<a href="fragment:([^"]*)">([\s\S]*?)<\/a>/g;

/** `EpubWriter.resolvingDocumentLinks`: links to documents (`<a href="fragment:<id>">`, kept only in an EPUB) pointed at
 *  the file holding that document; one not in the book becomes its words. */
function resolvingDocumentLinks(chapters: Chapter[]): Chapter[] {
  if (!chapters.some((c) => c.html.includes('href="fragment:'))) return chapters;
  const fileFor = new Map<string, string>();
  for (const c of chapters) for (const id of c.fragmentIDs) if (!fileFor.has(id)) fileFor.set(id, c.file);
  return chapters.map((c) => ({
    ...c,
    html: c.html.replace(DOCUMENT_LINK, (_, id: string, words: string) => {
      const file = fileFor.get(id);
      return file !== undefined ? `<a href="${file}">${words}</a>` : words;
    }),
  }));
}

export interface EpubOptions {
  /** `dcterms:modified`; defaults to now. */
  date?: Date;
}

/** ISO 8601 without fractional seconds, as `ISO8601DateFormatter` writes it. */
export const iso8601 = (d: Date) => d.toISOString().replace(/\.\d+/, '');

/** `EpubWriter.write`: the files of the EPUB in order (the zip is `epubArchive`). */
export function epubEntries(m: CompiledManuscript, o: EpubOptions = {}): [string, string][] {
  const r = m.preset.render;
  const all = chapters(m);
  // The in-book Contents: links to the parts, chapters and headed back matter.
  const listed = all.filter((c) => isListedInContents(c.role, c.matter));
  const contentsList = '<ol class="contents">\n' + listed.map((c) => `<li class="${c.role === 'part' ? 'part' : 'entry'}"><a href="${c.file}">${esc(c.title)}</a></li>`).join('\n') + '\n</ol>';
  const mark = m.readerMark !== null ? `<p class="reader-mark">${esc(m.readerMark)}</p>\n` : '';
  for (const c of all) c.html = c.html.replaceAll('<!--contents-->', contentsList) + mark + '</section>';
  const titlePage = titlePageContent(m);

  const language = m.language === '' ? 'en' : m.language;
  // The ebook's ISBN identifies it where there is one.
  const isbn = ISBN.check(isbnOf(m.publication, 'ebook'));
  const identifier = isbn.type === 'valid' ? `urn:isbn:${isbn.isbn13}` : `urn:uuid:${m.projectID}`;
  const publisher = trimCFWhitespace(m.publication.publisher);
  const modified = iso8601(o.date ?? new Date());
  const author = m.author ? bylineName(m.author) : '';
  const authors = m.author ? authorNames(m.author) : [];
  // One creator, as before; co-authors (W9c) each get theirs, refined as authors in byline order.
  const creators = authors.length > 1
    ? authors.map((name, i) => `<dc:creator id="creator${i + 1}">${esc(name)}</dc:creator>
<meta refines="#creator${i + 1}" property="role" scheme="marc:relators">aut</meta>
<meta refines="#creator${i + 1}" property="display-seq">${i + 1}</meta>`).join('\n')
    : author === '' ? '' : `<dc:creator>${esc(author)}</dc:creator>`;

  const files: [string, string][] = [];
  files.push(['mimetype', 'application/epub+zip']);
  files.push(['META-INF/container.xml', `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container"><rootfiles><rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/></rootfiles></container>`]);
  files.push(['OEBPS/style.css', epubCSS(r)]);
  // An ebook's title page is the title and author only: no contact block or word count.
  if (titlePage) {
    files.push(['OEBPS/title.xhtml', page(m.title, language, `<section epub:type="titlepage" class="titlepage">
<h1 class="title">${esc(titlePage.title)}</h1>
${titlePage.byline === null || author === '' ? '' : `<p class="author">${esc(author)}</p>`}
${mark}</section>`)]);
  }
  for (const c of all) files.push([`OEBPS/${c.file}`, page(c.title, language, c.html)]);
  const start = all.find((c) => c.role !== 'front-matter') ?? all[0];
  files.push(['OEBPS/nav.xhtml', page('Contents', language, `<nav epub:type="toc" role="doc-toc" id="toc"><h1>Contents</h1><ol>
${all.map((c) => `<li><a href="${c.file}">${esc(c.title)}</a></li>`).join('\n')}
</ol></nav>
<nav epub:type="landmarks" hidden="hidden"><ol><li><a epub:type="toc" href="nav.xhtml">Contents</a></li>${start ? `<li><a epub:type="bodymatter" href="${start.file}">Start</a></li>` : ''}</ol></nav>`)]);
  files.push(['OEBPS/content.opf', `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="book-id" xml:lang="${language}">
<metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
<dc:identifier id="book-id">${identifier}</dc:identifier>
<dc:title>${esc(m.title)}</dc:title>
${creators}
${publisher === '' ? '' : `<dc:publisher>${esc(publisher)}</dc:publisher>`}
<dc:language>${language}</dc:language>
${m.readerMark !== null ? `<dc:rights>${esc(m.readerMark)}</dc:rights>` : ''}
<meta property="dcterms:modified">${modified}</meta>
<meta property="schema:accessMode">textual</meta>
<meta property="schema:accessModeSufficient">textual</meta>
<meta property="schema:accessibilityFeature">structuralNavigation</meta>
<meta property="schema:accessibilityFeature">tableOfContents</meta>
<meta property="schema:accessibilityFeature">readingOrder</meta>
<meta property="schema:accessibilityHazard">none</meta>
<meta property="schema:accessibilitySummary">A text-only book with a navigable table of contents and semantic scene breaks.</meta>
</metadata>
<manifest>
<item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>
<item id="css" href="style.css" media-type="text/css"/>
${titlePage ? '<item id="title" href="title.xhtml" media-type="application/xhtml+xml"/>' : ''}
${all.map((c, i) => `<item id="s${i + 1}" href="${c.file}" media-type="application/xhtml+xml"/>`).join('\n')}
</manifest>
<spine>
${titlePage ? '<itemref idref="title"/>' : ''}
<itemref idref="nav" linear="no"/>
${all.map((_, i) => `<itemref idref="s${i + 1}"/>`).join('\n')}
</spine>
</package>`]);
  return files;
}

/** The EPUB as a zip: `mimetype` first and stored. */
export function writeEpub(m: CompiledManuscript, o: EpubOptions = {}): Uint8Array {
  const zip = new ZipWriter();
  for (const [name, text] of epubEntries(m, o)) zip.add(name, text, name === 'mimetype');
  return zip.finish();
}

/** A section's blocks as XHTML, lists wrapped; a paragraph opening the section or after a scene break has no indent. */
export function blocksHTML(blocks: Block[], allFirst: boolean, scope = ''): string {
  const out: string[] = [];
  let previous: Block | null = null;
  for (const block of blocks) {
    const tags = listTags(previous, block, false);
    if (tags) out.push(tags);
    out.push(blockHTML(block, allFirst || previous === null || previous.kind.type === 'sceneBreak', scope));
    previous = block;
  }
  const tags = listTags(previous, null, false);
  if (tags) out.push(tags);
  return out.join('\n');
}

function blockHTML(block: Block, first: boolean, scope: string): string {
  const content = inlinesHTML(block.inlines, scope);
  switch (block.kind.type) {
    case 'sceneBreak': return '<hr class="scene-break"/>';
    case 'paragraph':
      if (block.alignment === null) return first ? `<p class="first">${content}</p>` : `<p>${content}</p>`;
      return first ? `<p class="first ${block.alignment}">${content}</p>` : `<p class="${block.alignment}">${content}</p>`;
    case 'listItem': return `<li>${content}</li>`;
    case 'blockQuote': return `<blockquote><p>${content}</p></blockquote>`;
    case 'epigraph': return `<blockquote class="epigraph" epub:type="epigraph" role="doc-epigraph"><p>${content}</p></blockquote>`;
    case 'verse': return verseHTML(block, scope);
    case 'footnote': return `<aside epub:type="footnote" role="doc-footnote" id="${noteID(block.kind.id, scope)}"><p>${content}</p></aside>`;
  }
}

/** `EpubWriter.verse`: a stanza as a `div.verse` of a `p.line` per line, stepped by class (`in1`…`in6`), so a wrapped
 *  line hangs under its first word; centred or right-aligned verse by the div's class. */
function verseHTML(block: Block, scope: string): string {
  const alignment = block.alignment !== null ? (block.alignment === 'justify' ? '' : ` ${block.alignment}`) : '';
  const lines = verseLines(block).map((line) => {
    const step = line.indent > 0 && alignment === '' ? ` in${line.indent}` : '';
    const content = inlinesHTML(line.inlines, scope);
    return `<p class="line${step}">${content === '' ? '&#160;' : content}</p>`;
  });
  return `<div class="verse${alignment}">${lines.join('')}</div>`;
}

function inlinesHTML(inlines: Inline[], scope: string): string {
  return inlines.map((inline) => {
    switch (inline.type) {
      case 'text': {
        let s = esc(inline.text);
        if (inline.style.smallCaps) s = `<span class="sc">${s}</span>`;
        if (inline.style.underline) s = `<u>${s}</u>`;
        if (inline.style.bold) s = `<strong>${s}</strong>`;
        if (inline.style.italic) s = `<em>${s}</em>`;
        return s;
      }
      case 'lineBreak': return '<br/>';
      case 'footnoteReference': return `<a epub:type="noteref" role="doc-noteref" href="#${noteID(inline.id, scope)}"><sup>${esc(inline.id)}</sup></a>`;
      // Only web and mail addresses reach here (compile turns `fragment:` links into their words).
      case 'linkStart': return `<a href="${esc(inline.address)}">`;
      case 'linkEnd': return '</a>';
    }
  }).join('');
}

function page(title: string, language: string, body: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" xml:lang="${language}" lang="${language}">
<head><meta charset="utf-8"/><title>${esc(title)}</title><link rel="stylesheet" type="text/css" href="style.css"/></head>
<body>
${body}
</body>
</html>`;
}

export function epubCSS(r: RenderSettings): string {
  return `body { margin: 0 5%; line-height: ${swiftNumber(r.lineSpacing)}; }
p { margin: 0; text-indent: ${swiftNumber(r.firstLineIndent)}in; ${r.justify ? 'text-align: justify;' : ''} }
p.first, blockquote p { text-indent: 0; }
h1, h2 { text-align: center; margin: 2em 0 1em; font-weight: normal; }
p.reader-mark { text-align: center; text-indent: 0; font-size: 0.75em; color: #777; margin-top: 3em; }
p.subtitle { text-align: center; text-indent: 0; font-style: italic; margin-bottom: 2em; }
hr.scene-break { border: 0; margin: 1em 0; text-align: center; height: 1.5em; }
hr.scene-break::after { content: "${r.sceneBreak.replaceAll('"', '\\"')}"; }
blockquote { margin: 1em 2em; }
.sc { font-variant: small-caps; }
.titlepage { text-align: center; margin-top: 30%; }
.titlepage .author { text-indent: 0; margin-top: 2em; }
section.half-title, section.title-page { text-align: center; margin-top: 30%; }
section.half-title p, section.title-page p, section.dedication p, section.epigraph p { text-indent: 0; text-align: center; }
section.title-page p.first:first-child { font-size: 1.6em; margin-bottom: 1em; }
section.dedication, section.epigraph { margin-top: 30%; font-style: italic; }
section.copyright { margin-top: 20%; font-size: 0.8em; }
section.copyright p { text-indent: 0; margin-bottom: 0.8em; text-align: left; }
ol.contents { list-style: none; padding: 0; }
ol.contents li { margin: 0.3em 0; }
ol.contents li.part { margin-top: 1em; font-variant: small-caps; }
ul, ol { margin: 0.5em 0; padding-left: 2em; }
li { text-indent: 0; }
ul.circle { list-style-type: circle; }
ul.dash { list-style-type: "\\2013  "; }
@counter-style paren { system: extends decimal; suffix: ") "; }
ol.paren { list-style-type: paren; }
p.center, section p.center { text-align: center; text-indent: 0; }
p.right, section p.right { text-align: right; text-indent: 0; }
p.justify, section p.justify { text-align: justify; }
div.verse { margin: 1em 0; }
div.verse + div.verse { margin-top: 1em; }
div.verse p, section div.verse p { margin: 0; text-align: left; text-indent: -1.5em; padding-left: 1.5em; hyphens: none; -webkit-hyphens: none; }
div.verse p.in1 { padding-left: 3.5em; } div.verse p.in2 { padding-left: 5.5em; } div.verse p.in3 { padding-left: 7.5em; }
div.verse p.in4 { padding-left: 9.5em; } div.verse p.in5 { padding-left: 11.5em; } div.verse p.in6 { padding-left: 13.5em; }
div.verse.center p { text-align: center; text-indent: 0; padding-left: 0; }
div.verse.right p { text-align: right; text-indent: 0; padding-left: 0; }`;
}

/** `EpubWriter.samplePage`: the sample's first chapter as the ebook's XHTML with its stylesheet inlined, for a preview. */
export function epubSamplePage(m: CompiledManuscript): string {
  const r = m.preset.render;
  let body = '';
  let inChapter = false;
  for (const [index, section] of m.sections.entries()) {
    if (section.matter !== null) continue;
    let stop = false;
    switch (section.role) {
      case 'part': case 'chapter': {
        // A part's opening chapter shows beneath it; the next chapter ends the page.
        if (inChapter) { stop = true; break; }
        inChapter = section.role === 'chapter';
        const lines = heading(section.role === 'part' ? r.partHeading : r.chapterHeading, section);
        body += lines.map((line, i) => (i === 0 ? `<h2>${esc(line)}</h2>` : `<p class="subtitle">${esc(line)}</p>`)).join('\n') + '\n';
        break;
      }
      default:
        if (section.followsScene) body += '<hr class="scene-break"/>\n';
    }
    if (stop) break;
    if (section.blocks.length) body += blocksHTML(section.blocks, false, String(index + 1)) + '\n';
    if (section.role === 'scene' && [...body].length > 40_000) break;
  }
  const language = m.language === '' ? 'en' : m.language;
  return `<!DOCTYPE html>
<html lang="${language}"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>
html { background: #fffdf9; color: #2b2723; font: 18px/1.5 Georgia, serif; }
body { max-width: 34em; margin: 2.5em auto; padding: 0 1.5em; }
${epubCSS(r)}
</style></head>
<body><section>${body}</section></body></html>`;
}
